import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Layers,
  HelpCircle,
  ListChecks,
  Package,
  Users,
  LogOut,
  Gauge,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";

const ALL_NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Diagnósticos", end: true, roles: ["admin", "gestor", "consultant"] },
  { to: "/admin/sessoes", icon: Layers, label: "Sessões", roles: ["admin", "gestor"] },
  { to: "/admin/perguntas", icon: HelpCircle, label: "Perguntas", roles: ["admin", "gestor"] },
  { to: "/admin/produtos", icon: Package, label: "Produtos", roles: ["admin", "gestor"] },
  { to: "/admin/listas", icon: ListChecks, label: "Listas", roles: ["admin", "gestor"] },
  { to: "/admin/maturidade", icon: Gauge, label: "Maturidade", roles: ["admin", "gestor"] },
  { to: "/admin/usuarios", icon: Users, label: "Usuários", roles: ["admin"] },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = ALL_NAV.filter((it) => it.roles.includes(user?.role || "consultant"));

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#050608] text-white flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#1C1F26] sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-[#1C1F26]">
          <Logo size={36} />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all",
                  isActive
                    ? "bg-[#1C1F26] text-white border border-[#3A3F47]"
                    : "text-[#9aa3ad] hover:bg-[#1C1F26]/60 hover:text-white",
                ].join(" ")
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1C1F26]">
          <div className="px-2 mb-3 text-xs">
            <div className="text-[#6B8DB3] truncate">{user?.email}</div>
            <div className="text-[#22B673] text-[10px] uppercase tracking-wider">
              {user?.role}
            </div>
          </div>
          <Button
            onClick={doLogout}
            data-testid="logout-btn"
            variant="outline"
            className="w-full border-[#3A3F47] text-white hover:bg-[#1C1F26]"
          >
            <LogOut size={14} className="mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile header */}
        <div className="lg:hidden border-b border-[#1C1F26] px-4 h-16 flex items-center justify-between">
          <Logo size={32} withText={false} />
          <div className="flex gap-2">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `text-xs px-3 py-1.5 rounded-md ${
                    isActive
                      ? "bg-[#1C1F26] text-white"
                      : "text-[#6B8DB3]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <Button
              onClick={doLogout}
              size="sm"
              variant="ghost"
              className="text-[#9aa3ad]"
            >
              <LogOut size={14} />
            </Button>
          </div>
        </div>
        <main className="p-6 sm:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
